import React, { useState, useEffect, useCallback } from 'react';
import {
    Row, Col, Card, Statistic, Progress, Table, Spin, Typography, Tag, Divider,
    Drawer, Button, Space,
} from 'antd';
import {
    TeamOutlined, EditOutlined, ClockCircleOutlined, CheckCircleOutlined,
    CreditCardOutlined, BankOutlined, FileDoneOutlined, PieChartOutlined,
    RightOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import AdminLayout from '../Layout/AdminLayout';
import config from '../../config';

const { Title, Text } = Typography;

const pct = (part, total) => (total === 0 ? 0 : Math.round((part / total) * 1000) / 10);

const STATUS_CONFIG = {
    draft:     { color: 'default',    label: 'Chưa hoàn thiện' },
    submitted: { color: 'processing', label: 'Chờ thanh toán' },
    completed: { color: 'success',    label: 'Hoàn thành' },
};

// ── Drawer danh sách ─────────────────────────────────────────────────────────
const CriterionDrawer = ({ criterion, onClose }) => {
    const [students, setStudents]   = useState([]);
    const [loading, setLoading]     = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const navigate = useNavigate();

    const fetchList = useCallback((page = 1, pageSize = 20) => {
        if (!criterion) return;
        const token = localStorage.getItem('token');
        setLoading(true);
        axios
            .get(`${config.apiUrl}/api/v1/admin/student-list`, {
                params: { page, per_page: pageSize, missing: criterion.key },
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(res => {
                if (res.data.success) {
                    setStudents(res.data.data.map(item => ({
                        ...item,
                        full_name: item?.user?.name,
                        email: item?.user?.email,
                        status: item?.status || 'draft',
                    })));
                    setPagination({
                        current: res.data.meta.current_page,
                        pageSize: res.data.meta.per_page,
                        total: res.data.meta.total,
                    });
                }
            })
            .catch(err => { if (err.response?.status === 401) navigate('/login'); })
            .finally(() => setLoading(false));
    }, [criterion, navigate]);

    useEffect(() => {
        if (criterion) {
            setStudents([]);
            setPagination({ current: 1, pageSize: 20, total: 0 });
            fetchList(1, 20);
        }
    }, [criterion, fetchList]);

    const columns = [
        {
            title: 'Mã hồ sơ',
            dataIndex: 'student_code',
            key: 'student_code',
            width: 110,
            render: (val) => val || <Text type="secondary">—</Text>,
        },
        {
            title: 'Họ và tên',
            dataIndex: 'full_name',
            key: 'full_name',
        },
        {
            title: 'CCCD',
            dataIndex: 'identification',
            key: 'identification',
            width: 140,
            render: (val) => val || <Text type="secondary">—</Text>,
        },
        {
            title: 'Ngày sinh',
            dataIndex: 'date_of_birth',
            key: 'date_of_birth',
            width: 100,
            render: (d) => (d && dayjs(d).isValid() ? dayjs(d).format('DD/MM/YYYY') : '—'),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (s) => {
                const cfg = STATUS_CONFIG[s] || STATUS_CONFIG.draft;
                return <Tag color={cfg.color}>{cfg.label}</Tag>;
            },
        },
        {
            title: '',
            key: 'action',
            width: 48,
            render: (_, row) => (
                <Button
                    type="link"
                    size="small"
                    icon={<RightOutlined />}
                    onClick={() => window.open(`/student-profile/${row.id}`, '_blank')}
                />
            ),
        },
    ];

    return (
        <Drawer
            title={
                <Space>
                    <span>{criterion?.label}</span>
                    {pagination.total > 0 && <Tag color="red">{pagination.total.toLocaleString()} hồ sơ</Tag>}
                </Space>
            }
            open={!!criterion}
            onClose={onClose}
            width={900}
            styles={{ body: { padding: '16px 24px' } }}
        >
            <Table
                size="small"
                columns={columns}
                dataSource={students}
                rowKey="id"
                loading={loading}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100'],
                    showTotal: (total, range) => `${range[0]}–${range[1]} / ${total}`,
                    onChange: (page, pageSize) => fetchList(page, pageSize),
                }}
                scroll={{ x: true }}
            />
        </Drawer>
    );
};

// ── Trang chính ──────────────────────────────────────────────────────────────
const ProfileStats = () => {
    const [data, setData]                 = useState(null);
    const [loading, setLoading]           = useState(true);
    const [activeCriterion, setActiveCriterion] = useState(null);
    const navigate                        = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) { navigate('/login'); return; }

        axios
            .get(`${config.apiUrl}/api/v1/admin/profile-stats`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(res => { if (res.data.success) setData(res.data.data); })
            .catch(err => { if (err.response?.status === 401) navigate('/login'); })
            .finally(() => setLoading(false));
    }, [navigate]);

    if (loading) {
        return (
            <AdminLayout title="Thống kê hồ sơ">
                <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
            </AdminLayout>
        );
    }

    const { status, payment, draft_breakdown } = data;
    const draftPct     = pct(status.draft,     status.total);
    const submittedPct = pct(status.submitted, status.total);
    const completedPct = pct(status.completed, status.total);

    const breakdownColumns = [
        {
            title: 'Mục còn thiếu',
            dataIndex: 'label',
            key: 'label',
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: 'Số hồ sơ',
            dataIndex: 'count',
            key: 'count',
            width: 110,
            align: 'right',
            sorter: (a, b) => a.count - b.count,
            defaultSortOrder: 'descend',
            render: (val) => <Tag color="red">{val.toLocaleString()}</Tag>,
        },
        {
            title: `Tỷ lệ / tổng (${status.total.toLocaleString()})`,
            key: 'progress',
            render: (_, row) => {
                const p = pct(row.count, status.total);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Progress
                            percent={p}
                            size="small"
                            strokeColor="#ff4d4f"
                            style={{ flex: 1, minWidth: 120 }}
                        />
                        <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>{p}%</Text>
                    </div>
                );
            },
        },
        {
            title: '',
            key: 'action',
            width: 100,
            render: (_, row) => (
                <Button
                    type="link"
                    size="small"
                    icon={<RightOutlined />}
                    onClick={() => setActiveCriterion(row)}
                >
                    Xem DS
                </Button>
            ),
        },
    ];

    return (
        <AdminLayout title="Thống kê hồ sơ">

            {/* ── Trạng thái hồ sơ ── */}
            <Title level={4} style={{ marginBottom: 16 }}>
                <PieChartOutlined style={{ marginRight: 8 }} />
                Trạng thái hồ sơ
            </Title>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Tổng thí sinh"
                            value={status.total}
                            prefix={<TeamOutlined />}
                            valueStyle={{ color: '#1677ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Chưa hoàn thiện"
                            value={status.draft}
                            prefix={<EditOutlined />}
                            suffix={<Text type="secondary" style={{ fontSize: 14 }}>{draftPct}%</Text>}
                            valueStyle={{ color: '#8c8c8c' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Chờ thanh toán"
                            value={status.submitted}
                            prefix={<ClockCircleOutlined />}
                            suffix={<Text type="secondary" style={{ fontSize: 14 }}>{submittedPct}%</Text>}
                            valueStyle={{ color: '#1677ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Hoàn thành"
                            value={status.completed}
                            prefix={<CheckCircleOutlined />}
                            suffix={<Text type="secondary" style={{ fontSize: 14 }}>{completedPct}%</Text>}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Stacked bar */}
            <Card style={{ marginTop: 16 }}>
                <Row gutter={16} align="middle">
                    <Col xs={24} md={4}>
                        <Text strong>Tỷ lệ hoàn thiện</Text>
                    </Col>
                    <Col xs={24} md={20}>
                        <div style={{ display: 'flex', height: 24, borderRadius: 4, overflow: 'hidden' }}>
                            {draftPct > 0 && (
                                <div style={{ width: `${draftPct}%`, background: '#d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {draftPct > 5 && <Text style={{ fontSize: 11, color: '#595959' }}>{draftPct}%</Text>}
                                </div>
                            )}
                            {submittedPct > 0 && (
                                <div style={{ width: `${submittedPct}%`, background: '#1677ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {submittedPct > 5 && <Text style={{ fontSize: 11, color: '#fff' }}>{submittedPct}%</Text>}
                                </div>
                            )}
                            {completedPct > 0 && (
                                <div style={{ width: `${completedPct}%`, background: '#52c41a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {completedPct > 5 && <Text style={{ fontSize: 11, color: '#fff' }}>{completedPct}%</Text>}
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
                            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#d9d9d9', borderRadius: 2, marginRight: 4 }} />Chưa hoàn thiện</span>
                            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#1677ff', borderRadius: 2, marginRight: 4 }} />Chờ thanh toán</span>
                            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#52c41a', borderRadius: 2, marginRight: 4 }} />Hoàn thành</span>
                        </div>
                    </Col>
                </Row>
            </Card>

            <Divider />

            {/* ── Thanh toán ── */}
            <Title level={4} style={{ marginBottom: 16 }}>
                <CreditCardOutlined style={{ marginRight: 8 }} />
                Thanh toán lệ phí
            </Title>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title="Biên lai thủ công"
                            value={payment.manual}
                            prefix={<FileDoneOutlined />}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>student_payments (không qua BIDV)</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title="Qua BIDV"
                            value={payment.bidv}
                            prefix={<BankOutlined />}
                            valueStyle={{ color: '#1677ff' }}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>payment_logs</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title="Tổng đã thanh toán (unique)"
                            value={payment.total}
                            prefix={<CheckCircleOutlined />}
                            suffix={<Text type="secondary" style={{ fontSize: 14 }}>{pct(payment.total, status.total)}%</Text>}
                            valueStyle={{ color: '#52c41a' }}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>Unique profile từ cả 2 nguồn</Text>
                    </Card>
                </Col>
            </Row>

            <Divider />

            {/* ── Phân tích draft ── */}
            <Title level={4} style={{ marginBottom: 8 }}>
                <EditOutlined style={{ marginRight: 8 }} />
                Phân tích lý do chưa hoàn thiện
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Một hồ sơ có thể thiếu nhiều mục đồng thời — tổng tỷ lệ có thể vượt 100%.
                Nhấn <Text strong>Xem DS</Text> để xem danh sách từng tiêu chí.
            </Text>
            <Table
                size="middle"
                columns={breakdownColumns}
                dataSource={draft_breakdown}
                rowKey="key"
                pagination={false}
                bordered
                onRow={(row) => ({
                    style: { cursor: 'pointer' },
                    onClick: () => setActiveCriterion(row),
                })}
            />

            {/* ── Drawer danh sách ── */}
            <CriterionDrawer
                criterion={activeCriterion}
                onClose={() => setActiveCriterion(null)}
            />
        </AdminLayout>
    );
};

export default ProfileStats;
